import { useQuery } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';

export interface PractitionerCredits {
  id: string;
  user_id: string;
  credits_balance: number;
  total_credits_purchased: number;
  total_credits_used: number;
  // Note: monthly_free_credits/monthly_credits_used are in practitioner_credits (legacy table)
  // user_credits is the new universal table for all users
  created_at: string;
  updated_at: string;
}

// Legacy practitioner credits with subscription tier info
export interface LegacyPractitionerCredits extends PractitionerCredits {
  monthly_free_credits: number;
  monthly_credits_used: number;
  subscription_tier?: string | null;
  last_monthly_reset?: string | null;
}

export interface CreditTransaction {
  id: string;
  practitioner_id: string;
  transaction_type: 'purchase' | 'deduction' | 'refund' | 'adjustment';
  credits_amount: number;
  session_id?: string;
  package_id?: string;
  stripe_payment_intent_id?: string;
  notes?: string;
  created_at: string;
}

export const usePractitionerCredits = (practitionerId?: string) => {
  const { user } = useAuth();
  const targetId = practitionerId || user?.id;

  const {
    data: credits,
    isLoading: creditsLoading,
    error: creditsError,
    refetch: refetchCredits
  } = useQuery({
    queryKey: ['user-credits', targetId],
    queryFn: async () => {
      if (!targetId) return null;

      const { data, error } = await backendClient
        .from('user_credits')
        .select('*')
        .eq('user_id', targetId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data as PractitionerCredits | null;
    },
    enabled: !!targetId,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  const {
    data: transactions = [],
    isLoading: transactionsLoading,
    error: transactionsError
  } = useQuery({
    queryKey: ['credit-transactions', targetId],
    queryFn: async () => {
      if (!targetId) return [];
      
      const { data, error } = await backendClient
        .from('credit_transactions')
        .select('*')
        .eq('practitioner_id', targetId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as CreditTransaction[];
    },
    enabled: !!targetId,
    staleTime: 60 * 1000, // 1 minute
  });

  const totalAvailableCredits = credits ? credits.credits_balance : 0;
  // Note: monthly free credits are tracked in practitioner_credits (legacy table)
  // For now, we only track total credits_balance in user_credits
  const monthlyFreeAvailable = 0; // Not available in user_credits table

  // Updated credit warning thresholds
  const isLowCredits = totalAvailableCredits < 30; // Changed from 10 to 30
  const isCriticalCredits = totalAvailableCredits < 10; // New critical threshold
  const isOutOfCredits = totalAvailableCredits <= 0;

  // Flag to indicate if query is enabled (user exists)
  // This helps consumers know if credit data is valid vs defaulted to 0
  const isEnabled = !!targetId;

  return {
    credits,
    transactions,
    totalAvailableCredits,
    monthlyFreeAvailable,
    isLoading: creditsLoading || transactionsLoading,
    isEnabled,
    error: creditsError || transactionsError,
    isLowCredits,
    isCriticalCredits,
    isOutOfCredits,
    refetchCredits,
  };
};
